import apiClient from './apiClient';
import { saveOffline, getOfflineData, deleteOfflineData, STORES, openDB } from '../utils/offlineDB';

// Lokaler Speicher für den aktuellen Offline-Timer
let currentOfflineTimer = null;

export const zeiterfassungApi = {
  // Timer starten (mit Offline-Support)
  start: async (data) => {
    try {
      const response = await apiClient.post('/zeiterfassung/start', data);
      return response.data;
    } catch (error) {
      // If offline, save locally
      if (!navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        console.log('Offline: Zeiterfassung wird lokal gestartet');
        const startzeit = new Date().toISOString();
        const offlineId = `offline-${Date.now()}`;

        // Lokalen Timer speichern
        currentOfflineTimer = {
          id: offlineId,
          ...data,
          startzeit,
          status: 'laufend',
          offline: true
        };

        return {
          message: 'Offline gespeichert - wird synchronisiert sobald online',
          offline: true,
          zeiterfassung: currentOfflineTimer
        };
      }
      throw error;
    }
  },

  // Timer pausieren
  pause: async (id) => {
    const response = await apiClient.post(`/zeiterfassung/${id}/pause`);
    return response.data;
  },

  // Timer fortsetzen
  resume: async (id) => {
    const response = await apiClient.post(`/zeiterfassung/${id}/resume`);
    return response.data;
  },

  // Timer stoppen (mit Offline-Support)
  stop: async (id, offlineTimerData = null) => {
    // Prüfen ob es ein Offline-Timer ist
    if (id && id.startsWith('offline-')) {
      // Offline-Timer stoppen und für Sync speichern
      const endzeit = new Date().toISOString();
      const timerData = offlineTimerData || currentOfflineTimer;

      if (!timerData) {
        throw new Error('Offline-Timer nicht gefunden');
      }

      // Berechne Gesamtminuten
      const startMs = new Date(timerData.startzeit).getTime();
      const endMs = new Date(endzeit).getTime();
      const gesamtminuten = Math.round((endMs - startMs) / 1000 / 60);

      // Speichere als abgeschlossene Zeiterfassung für Sync
      await saveOffline(STORES.ZEITERFASSUNG, {
        type: 'completed',
        data: {
          terminId: timerData.terminId,
          katalogPositionId: timerData.katalogPositionId,
          notiz: timerData.notiz,
          startzeit: timerData.startzeit,
          endzeit: endzeit,
          gesamtminuten: gesamtminuten,
          stundensatz: timerData.stundensatz || 0
        },
        timestamp: Date.now()
      });

      // Lokalen Timer zurücksetzen
      currentOfflineTimer = null;

      console.log(`📴 Offline-Timer gestoppt: ${gesamtminuten} Minuten`);

      return {
        message: 'Timer gestoppt (wird synchronisiert sobald online)',
        offline: true,
        zusammenfassung: {
          arbeitszeit: `${Math.floor(gesamtminuten / 60)}:${String(gesamtminuten % 60).padStart(2, '0')}`,
          pausenzeit: '0:00',
          stundensatz: timerData.stundensatz || 0,
          kostenFormatiert: `${((gesamtminuten / 60) * (timerData.stundensatz || 0)).toFixed(2)} €`
        }
      };
    }

    // Online-Timer stoppen
    const response = await apiClient.post(`/zeiterfassung/${id}/stop`);
    return response.data;
  },

  // Aktuellen Offline-Timer abrufen
  getCurrentOfflineTimer: () => currentOfflineTimer,

  // Offline-Timer setzen (z.B. nach App-Neustart)
  setCurrentOfflineTimer: (timer) => {
    currentOfflineTimer = timer;
  },

  // Manuelle Zeiterfassung erstellen
  createManual: async (data) => {
    const response = await apiClient.post('/zeiterfassung/manual', data);
    return response.data;
  },

  // Zeiterfassungen eines Termins laden
  getByTermin: async (terminId) => {
    const response = await apiClient.get(`/zeiterfassung/termin/${terminId}`);
    return response.data;
  },

  // Aktiven Timer eines Termins laden
  getAktiverTimer: async (terminId) => {
    const response = await apiClient.get(`/zeiterfassung/termin/${terminId}/aktiv`);
    return response.data;
  },

  // Zeiterfassung bearbeiten
  update: async (id, data) => {
    const response = await apiClient.put(`/zeiterfassung/${id}`, data);
    return response.data;
  },

  // Zeiterfassung löschen
  delete: async (id) => {
    const response = await apiClient.delete(`/zeiterfassung/${id}`);
    return response.data;
  },

  // In Rechnung übernehmen
  toRechnung: async (id, rechnungId) => {
    const response = await apiClient.post(`/zeiterfassung/${id}/to-rechnung`, {
      rechnungId
    });
    return response.data;
  },

  // Statistiken laden
  getStats: async (zeitraum = 'monat') => {
    const response = await apiClient.get('/zeiterfassung/stats', {
      params: { zeitraum }
    });
    return response.data;
  },

  // Offline-Daten synchronisieren
  syncOfflineData: async () => {
    if (!navigator.onLine) {
      console.log('Noch offline - kann nicht synchronisieren');
      return { synced: 0, failed: 0 };
    }

    try {
      const pendingData = await getOfflineData(STORES.ZEITERFASSUNG);
      let synced = 0;
      let failed = 0;

      for (const item of pendingData) {
        try {
          if (item.data.type === 'start') {
            // Nur Start speichern (läuft noch)
            await apiClient.post('/zeiterfassung/start', item.data.data);
          } else if (item.data.type === 'manual') {
            // Manuelle Eingabe
            await apiClient.post('/zeiterfassung/manual', item.data.data);
          } else if (item.data.type === 'completed') {
            // Abgeschlossene Zeiterfassung - als manuelle Eingabe synchronisieren
            const completedData = item.data.data;
            await apiClient.post('/zeiterfassung/manual', {
              terminId: completedData.terminId,
              katalogPositionId: completedData.katalogPositionId,
              notiz: completedData.notiz ? `${completedData.notiz} (Offline erfasst)` : '(Offline erfasst)',
              startzeit: completedData.startzeit,
              endzeit: completedData.endzeit,
              gesamtminuten: completedData.gesamtminuten
            });
            console.log(`✅ Offline-Zeiterfassung synchronisiert: ${completedData.gesamtminuten} Minuten`);
          }

          await deleteOfflineData(STORES.ZEITERFASSUNG, item.id);
          synced++;
          console.log('Zeiterfassung synchronisiert:', item.id);
        } catch (error) {
          console.error('Sync fehlgeschlagen für:', item.id, error);
          failed++;
        }
      }

      return { synced, failed };
    } catch (error) {
      console.error('Offline sync error:', error);
      return { synced: 0, failed: 0 };
    }
  },

  // Anzahl der ausstehenden Offline-Einträge
  getPendingOfflineCount: async () => {
    try {
      const pendingData = await getOfflineData(STORES.ZEITERFASSUNG);
      return pendingData.length;
    } catch (error) {
      return 0;
    }
  }
};

export default zeiterfassungApi;
